import { BillExtractionRecord } from "@common/schemas/financialDocument/bill-extraction-record.schema";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import { FIELD_UPDATE_SOURCE } from "@common/constants";
import { isUserAssignedCounterpartySource } from "@common/constants/counterparty-assignment";
import { AccrualImporterRow } from "@components/accrualImporter/types";
import { createAccrualImporterRowLineId } from "@components/accrualImporter/accrualImporterLineId.helpers";
import { computeAccrualLineAmount } from "@common/helpers/accrual-line-item-amount.helper";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { parseAccrualImporterDateToIso } from "@components/accrualImporter/accrualImporterDateFormat";

export interface RowSnapshot {
    item: string | null;
    description: string | null;
    amount: number;
    quantity: number | null;
    unitPrice: number | null;
    classIds: string[] | null;
    accountId: string | null;
    accrualAccountId: string | null;
    catalogItemId: string | null;
    catalogItemPriceId: string | null;
}

export interface DetailsSnapshot {
    counterpartyId: string | null;
    counterpartyName: string | null;
    documentNumber: string | null;
    documentDate: string | null;
    dueDate: string | null;
    totalAmount: number | null;
    taxAmount: number | null;
    memo: string | null;
    entityId: number | null;
}

export const MAX_ACCRUAL_CONTENT_ROWS = 500;
const MAX_CONTENT_ROW_INDEX = MAX_ACCRUAL_CONTENT_ROWS - 1;

export const ACCRUAL_CONTENT_ROW_LIMIT_MESSAGE = `Saving is limited to the first ${MAX_ACCRUAL_CONTENT_ROWS} line items`;

function toRowSnapshot(row: AccrualImporterRow): RowSnapshot {
    const quantity = row.quantity;
    const unitPrice = row.unitPrice;
    const amount =
        computeAccrualLineAmount({
            quantity,
            unitPrice,
            amount: row.amount,
        }) ?? 0;

    return {
        item: row.productOrService || null,
        description: row.description || null,
        amount,
        quantity,
        unitPrice,
        classIds: row.classIds,
        accountId: row.accrualLedger || null,
        accrualAccountId: row.accrualAccountId || null,
        catalogItemId: row.catalogItemId,
        catalogItemPriceId: row.catalogItemPriceId,
    };
}

function normalizeClassIds(classIds: string[] | null): string {
    if (!classIds) {
        return "null";
    }
    return JSON.stringify([...classIds].sort((a, b) => a.localeCompare(b)));
}

export function isRowEqual(a: RowSnapshot, b: RowSnapshot): boolean {
    return (
        a.item === b.item &&
        a.description === b.description &&
        a.amount === b.amount &&
        a.quantity === b.quantity &&
        a.unitPrice === b.unitPrice &&
        normalizeClassIds(a.classIds) === normalizeClassIds(b.classIds) &&
        a.accountId === b.accountId &&
        a.accrualAccountId === b.accrualAccountId &&
        a.catalogItemId === b.catalogItemId &&
        a.catalogItemPriceId === b.catalogItemPriceId
    );
}

function hasContent(row: AccrualImporterRow): boolean {
    return (
        Boolean(row.productOrService?.trim()) ||
        Boolean(row.description?.trim()) ||
        row.amount !== null ||
        row.quantity !== null ||
        row.unitPrice !== null ||
        Boolean(row.classIds?.length) ||
        Boolean(row.accrualLedger?.trim()) ||
        Boolean(row.accrualAccountId?.trim()) ||
        row.catalogItemId != null ||
        row.catalogItemPriceId != null
    );
}

function resolveCounterpartyName({
    counterpartyId,
    counterpartiesMap,
}: {
    counterpartyId: string | null;
    counterpartiesMap: Map<string, string>;
}): string | null {
    if (!counterpartyId) {
        return null;
    }
    return counterpartiesMap.get(counterpartyId) ?? null;
}

function forEachCappedContentRow(
    rows: AccrualImporterRow[],
    onRow: (row: AccrualImporterRow, contentRowIndex: number) => void,
): { hasTruncatedRows: boolean } {
    let contentRowIndex = 0;
    let hasTruncatedRows = false;

    for (const row of rows) {
        if (!hasContent(row)) {
            continue;
        }

        if (contentRowIndex > MAX_CONTENT_ROW_INDEX) {
            hasTruncatedRows = true;
            break;
        }

        onRow(row, contentRowIndex);
        contentRowIndex += 1;
    }

    return { hasTruncatedRows };
}

export function buildContentRowSnapshots(rows: AccrualImporterRow[]): {
    snapshots: Map<number, RowSnapshot>;
    hasTruncatedRows: boolean;
} {
    const snapshots = new Map<number, RowSnapshot>();
    const { hasTruncatedRows } = forEachCappedContentRow(
        rows,
        (row, rowIndex) => {
            snapshots.set(rowIndex, toRowSnapshot(row));
        },
    );

    return { snapshots, hasTruncatedRows };
}

export function toDetailsSnapshot({
    firstRow,
    counterpartiesMap,
}: {
    firstRow: AccrualImporterRow | undefined;
    counterpartiesMap: Map<string, string>;
}): DetailsSnapshot {
    if (!firstRow) {
        return {
            counterpartyId: null,
            counterpartyName: null,
            documentNumber: null,
            documentDate: null,
            dueDate: null,
            totalAmount: null,
            taxAmount: null,
            memo: null,
            entityId: null,
        };
    }

    return {
        counterpartyId: firstRow.counterpartyId,
        counterpartyName:
            resolveCounterpartyName({
                counterpartyId: firstRow.counterpartyId,
                counterpartiesMap,
            }) ??
            firstRow.counterpartyName ??
            null,
        documentNumber: firstRow.invoiceNumber || null,
        documentDate: parseAccrualImporterDateToIso(firstRow.createdDate),
        dueDate: parseAccrualImporterDateToIso(firstRow.dueDate),
        totalAmount: firstRow.totalAmount ?? null,
        taxAmount: firstRow.tax ?? null,
        memo: firstRow.memo || null,
        entityId: firstRow.entityId,
    };
}

export function isDetailsEqual(
    a: DetailsSnapshot,
    b: DetailsSnapshot,
): boolean {
    return (
        a.counterpartyId === b.counterpartyId &&
        a.counterpartyName === b.counterpartyName &&
        a.documentNumber === b.documentNumber &&
        a.documentDate === b.documentDate &&
        a.dueDate === b.dueDate &&
        a.totalAmount === b.totalAmount &&
        a.memo === b.memo &&
        a.entityId === b.entityId
    );
}

export function buildDetailsPatch({
    previous,
    next,
    firstRow,
}: {
    previous: DetailsSnapshot | null;
    next: DetailsSnapshot;
    firstRow: AccrualImporterRow | undefined;
}): Partial<DetailsSnapshot> {
    const patch: Partial<DetailsSnapshot> = {};

    const skipCounterpartyNamePatch =
        Boolean(firstRow?.counterpartyId) &&
        firstRow?.counterpartyId === previous?.counterpartyId &&
        next.counterpartyName === null &&
        Boolean(previous?.counterpartyName);

    if (
        (!previous || previous.counterpartyName !== next.counterpartyName) &&
        !skipCounterpartyNamePatch
    ) {
        patch.counterpartyName = next.counterpartyName;
    }

    if (!previous || previous.counterpartyId !== next.counterpartyId) {
        patch.counterpartyId = next.counterpartyId;
    }

    if (!previous || previous.documentNumber !== next.documentNumber) {
        patch.documentNumber = next.documentNumber;
    }

    if (!previous || previous.documentDate !== next.documentDate) {
        patch.documentDate = next.documentDate;
    }

    if (!previous || previous.dueDate !== next.dueDate) {
        patch.dueDate = next.dueDate;
    }

    if (!previous || previous.totalAmount !== next.totalAmount) {
        patch.totalAmount = next.totalAmount;
    }

    if (!previous || previous.memo !== next.memo) {
        patch.memo = next.memo;
    }

    if (!previous || previous.entityId !== next.entityId) {
        patch.entityId = next.entityId;
    }

    return patch;
}

function createLineId(row: AccrualImporterRow, extractionId: string): string {
    if (row.lineId) {
        return row.lineId;
    }
    return createAccrualImporterRowLineId({
        scopeId: extractionId,
        rowId: row._id,
    });
}

function buildLineItemsFromRows(
    rows: AccrualImporterRow[],
    extractionId: string,
) {
    const lineItems: Array<{
        lineId: string;
        item: string | null;
        description: string | null;
        amount: number;
        quantity: number | null;
        unitPrice: number | null;
        classIds: string[] | null;
        accountId: string | null;
        accrualAccountId: string | null;
        catalogItemId: string | null;
        catalogItemPriceId: string | null;
    }> = [];

    forEachCappedContentRow(rows, (row) => {
        const quantity = row.quantity;
        const unitPrice = row.unitPrice;
        const amount =
            computeAccrualLineAmount({
                quantity,
                unitPrice,
                amount: row.amount,
            }) ?? 0;

        lineItems.push({
            lineId: createLineId(row, extractionId),
            item: row.productOrService || null,
            description: row.description || null,
            amount,
            quantity,
            unitPrice,
            classIds: row.classIds,
            accountId: row.accrualLedger || null,
            accrualAccountId: row.accrualAccountId || null,
            catalogItemId: row.catalogItemId,
            catalogItemPriceId: row.catalogItemPriceId,
        });
    });

    return lineItems;
}

function sumLineItemAmountsWithAdjustments({
    lineItems,
    taxAmount,
    discountAmount,
}: {
    lineItems: Array<{ amount: number }>;
    taxAmount: number | null;
    discountAmount: number | null;
}): number | null {
    if (lineItems.length === 0) {
        return null;
    }
    return sumMonetaryValues([
        ...lineItems.map((lineItem) => lineItem.amount),
        taxAmount ?? 0,
        -(discountAmount ?? 0),
    ]);
}

function resolveCounterpartyIdForReplacement({
    detailsCounterpartyId,
    detailsCounterpartyName,
    previousCounterpartyId,
    previousSource,
}: {
    detailsCounterpartyId: string | null;
    detailsCounterpartyName: string | null;
    previousCounterpartyId: string | null;
    previousSource: FIELD_UPDATE_SOURCE | null;
}): string | null {
    if (detailsCounterpartyId !== null) {
        return detailsCounterpartyId;
    }

    if (
        previousCounterpartyId !== null &&
        previousSource !== null &&
        !isUserAssignedCounterpartySource(previousSource) &&
        detailsCounterpartyName !== null
    ) {
        return previousCounterpartyId;
    }

    return null;
}

export function rowsToInvoiceExtractionRecord({
    rows,
    extractionId,
    counterpartiesMap,
    existingExtraction,
}: {
    rows: AccrualImporterRow[];
    extractionId: string;
    counterpartiesMap: Map<string, string>;
    existingExtraction?: InvoiceExtractionRecord | null;
}): InvoiceExtractionRecord {
    const details = toDetailsSnapshot({
        firstRow: rows[0],
        counterpartiesMap,
    });
    const lineItems = buildLineItemsFromRows(rows, extractionId);
    const taxAmount =
        details.taxAmount ?? existingExtraction?.taxAmount ?? null;
    const discountAmount = existingExtraction?.discountAmount ?? null;
    const previousCounterpartyId = existingExtraction?.counterpartyId ?? null;
    const previousSource = existingExtraction?.counterpartyIdSource ?? null;
    const counterpartyId = resolveCounterpartyIdForReplacement({
        detailsCounterpartyId: details.counterpartyId,
        detailsCounterpartyName: details.counterpartyName,
        previousCounterpartyId,
        previousSource,
    });
    let counterpartyIdSource = previousSource;
    if (counterpartyId !== previousCounterpartyId) {
        counterpartyIdSource =
            counterpartyId !== null ? FIELD_UPDATE_SOURCE.ACTIONS : null;
    }

    return {
        id: extractionId,
        counterpartyId,
        counterpartyIdSource,
        counterpartyName: details.counterpartyName,
        documentNumber: details.documentNumber,
        documentDate: details.documentDate,
        dueDate: details.dueDate,
        totalAmount:
            details.totalAmount ??
            existingExtraction?.totalAmount ??
            sumLineItemAmountsWithAdjustments({
                lineItems,
                taxAmount,
                discountAmount,
            }),
        entityId: details.entityId,
        memo: details.memo,
        taxAmount,
        discountAmount,
        balanceDue: existingExtraction?.balanceDue ?? null,
        paymentDate: existingExtraction?.paymentDate ?? null,
        warnings:
            existingExtraction?.warnings ?? rows[0]?.extractionWarnings ?? null,
        lineItems,
    };
}

export function rowsToBillExtractionRecord({
    rows,
    extractionId,
    counterpartiesMap,
    existingExtraction,
}: {
    rows: AccrualImporterRow[];
    extractionId: string;
    counterpartiesMap: Map<string, string>;
    existingExtraction?: BillExtractionRecord | null;
}): BillExtractionRecord {
    const invoiceRecord = rowsToInvoiceExtractionRecord({
        rows,
        extractionId,
        counterpartiesMap,
        existingExtraction,
    });

    return {
        ...invoiceRecord,
        lineItems: invoiceRecord.lineItems.map(
            ({ catalogItemId, catalogItemPriceId, ...billLineItem }) =>
                billLineItem,
        ),
    };
}
